import React, { useEffect, useState } from 'react';
import Stack from '@mui/material/Stack';
import { useTranslation } from 'react-i18next';
import { makeStyles } from 'tss-react/mui';
import { useNavigate, useSearchParams } from 'react-router-dom';
import NavigationButton from './NavigationButton';
import {
  CloseIcon,
  OpenIcon,
  OrganizationsIcon,
  OpenInNewTabIcon,
  DashboardIcon,
  StatisticsIcon,
  MissionHistoryIcon,
  UsersIcon,
  SettingsIcon,
  AdministrationIcon,
} from '@common/utils/icons/icons';
import { Theme } from '@mui/material/styles';
import { useAppDispatch, useAppSelector } from '../../store';
import {
  selectCurrentOrganizationId,
  selectOrganizationsData,
} from '../../store/organization/organization-slice-selectors';
import OrganizationRole from '../../models/enums/organization-roles';
import { FilterKeys, navigateSphereWithFilters } from '@common/enums/query-params/filters';
import Box from '@mui/material/Box';
import Fab from '@mui/material/Fab';
import Divider from '@mui/material/Divider';
import useMediaQuery from "@mui/material/useMediaQuery";
import Zoom from '@mui/material/Zoom';
import { selectIsUserAdminOrHigher, selectUser } from '../../store/general/general-slice-selectors';
import {
  resetOrganizationsRestState,
  setOrganizationWebSocketStatus,
} from 'store/organization/organization-slice';
import WebSocketConnectionStatus from 'models/enums/websocket-connection-status';
import WebSocketMessage from 'models/misc/api/websocket-message';
import { handleWebsocketMessage, resetRobotsState } from 'store/robots/robots-slice';
import useWebsocketEffect from 'hooks/useWebsocketEffect';
import { getOrganizationById } from 'store/organization/organization-actions';
import HttpFetchStatus from '@common/enums/api/http-fetch-status';
import SphereNavigationRoutes from '@common/enums/query-params/sphere-navigation-routes';
import { normalizePathname } from 'utils/route-matching';
import AdditionalButtons from './AdditionalButtons';

const useStyles = makeStyles()(() => ({
  navContainer: {
    transition: 'all 0.4s ease-in-out',
    overflowX: 'hidden',
  },
}));

export const navigationOpenStorageName = 'sphere_navBarState';

const MainNavigationBar: React.FC<{ isNavOpen: boolean; onNavChangeHandler: () => void }> = ({
  isNavOpen,
  onNavChangeHandler,
}) => {
  const { classes } = useStyles();
  const { t } = useTranslation();

  const orgId = window.location.pathname.split('/')[1];
  const [filterParams] = useSearchParams();
  const queryRobotId = filterParams.get(FilterKeys.RobotId);
  const queryUserId = filterParams.get(FilterKeys.UserId);
  const queryReport = filterParams.get(FilterKeys.Report);
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const matchesSmDown = useMediaQuery((theme: Theme) => theme.breakpoints.down('sm'));
  const matchesLgDown = useMediaQuery((theme: Theme) => theme.breakpoints.down('lg'));

  const userData = useAppSelector((state) => selectUser(state));
  const isAdmin = useAppSelector((state) => selectIsUserAdminOrHigher(state));
  const organizationData = useAppSelector((state) => selectOrganizationsData(state));
  const currentOrganizationId = useAppSelector((state) => selectCurrentOrganizationId(state));

  const [isToggleSideBarButtonVisible, setIsToggleSideBarButtonVisible] = useState(false);

  const publicPageIgnoredId = [
    SphereNavigationRoutes.Legal,
    SphereNavigationRoutes.Unsubscribe,
    SphereNavigationRoutes.AccountData,
  ].some((pageRoute) => orgId === pageRoute.replace('/', ''));

  useEffect(() => {
    if (
      userData.data.organization.id.length > 0 &&
      orgId.length > 0 &&
      organizationData.rest.getStatus === HttpFetchStatus.Idle &&
      !publicPageIgnoredId
    ) {
      dispatch(getOrganizationById(orgId));
    }
  }, [
    orgId,
    dispatch,
    organizationData.rest.getStatus,
    userData.data.organization.id.length,
    publicPageIgnoredId,
  ]);

  useEffect(() => {
    if (
      userData.data.organization.id.length > 0 &&
      !window.location.pathname.includes(userData.data.organization.id) &&
      (publicPageIgnoredId ||
        orgId.length === 0 ||
        (organizationData.rest.getStatus === HttpFetchStatus.Failed &&
          organizationData.rest.getError?.code === '404'))
    ) {
      let path = userData.data.organization.id;
      if (publicPageIgnoredId) {
        path = path + normalizePathname(window.location.pathname);
      }

      navigate({ pathname: `/${path}`, search: window.location.search });
    }
  }, [
    navigate,
    orgId,
    organizationData.rest.getError?.code,
    organizationData.rest.getStatus,
    queryRobotId,
    queryReport,
    queryUserId,
    userData.data.organization.id,
    publicPageIgnoredId,
  ]);

  useEffect(() => {
    if (organizationData.rest.getStatus === HttpFetchStatus.Success) {
      dispatch(resetRobotsState());
    }
    if (
      orgId.length > 0 &&
      organizationData.rest.getStatus === HttpFetchStatus.Success &&
      orgId !== currentOrganizationId
    ) {
      dispatch(resetOrganizationsRestState({ getStatus: HttpFetchStatus.Idle, getError: null }));
    }
  }, [currentOrganizationId, dispatch, orgId, organizationData.rest.getStatus]);

  const navigateToRouteHandler = (route: SphereNavigationRoutes) => {
    navigate(navigateSphereWithFilters(`${currentOrganizationId}${route}`, true));
    if (matchesSmDown) {
      onNavChangeHandler();
    }
  };

  const mouseOverNavHandler = () => {
    setIsToggleSideBarButtonVisible(true);
  };

  const mouseOffNavHandler = () => {
    setIsToggleSideBarButtonVisible(false);
  };

  const toggleSideBarClickHandler = () => {
    setIsToggleSideBarButtonVisible(false);
    onNavChangeHandler();
  };

  const isPageOpen = (route: SphereNavigationRoutes) => {
    const pathnameList = normalizePathname(window.location.pathname).split('/');
    return (
      (pathnameList.length === 2 && route === SphereNavigationRoutes.Dashboard) ||
      route.replace('/', '') === pathnameList[pathnameList.length - 1]
    );
  };

  const openSocketConnectionHandler = () => {
    dispatch(setOrganizationWebSocketStatus(WebSocketConnectionStatus.CONNECTED));
  };

  const receiveSocketMessageHandler = (message: WebSocketMessage) => {
    dispatch(handleWebsocketMessage(message));
  };

  const closeSocketConnectionHandler = () => {
    dispatch(setOrganizationWebSocketStatus(WebSocketConnectionStatus.DISCONNECTED));
  };

  useWebsocketEffect(
    `organization/${currentOrganizationId}`,
    openSocketConnectionHandler,
    receiveSocketMessageHandler,
    closeSocketConnectionHandler,
    currentOrganizationId == null || currentOrganizationId.length === 0,
  );

  const newTabClickHandler = (
    url:
      | string
      | {
        pathname: string;
        search: string;
      },
  ) => {
    window.open(url.toString(), '_blank');
  };

  return (
    <React.Fragment>
      <Box
        position="relative"
        minWidth="min-content"
        sx={{ px: 1, pb: { xs: 0, sm: 1 } }}
        onMouseEnter={mouseOverNavHandler}
        onMouseLeave={mouseOffNavHandler}
      >
        {!matchesSmDown && (
          <Box
            sx={(theme) => ({
              position: 'absolute',
              top: theme.spacing(4.5),
              right: theme.spacing(-2),
              borderRadius: '50%',
              zIndex: theme.zIndex.fab,
            })}
          >
            <Zoom in={isToggleSideBarButtonVisible} timeout={100} unmountOnExit>
              <Fab
                onClick={toggleSideBarClickHandler}
                size="small"
                sx={(theme) => ({
                  boxShadow: 2,
                  background: theme.palette.background.default,
                  ':hover': { backgroundColor: theme.palette.action.hover },
                })}
              >
                {isNavOpen ? <CloseIcon /> : <OpenIcon />}
              </Fab>
            </Zoom>
          </Box>
        )}
        <Stack
          data-testid="nav-bar-container"
          className={classes.navContainer}
          sx={(theme) => ({
            width: { xs: '100%', sm: isNavOpen ? theme.spacing(32) : theme.spacing(7) },
            height: '100%',
            overflow: 'hidden',
          })}
          spacing={1}
        >
          <Stack sx={{ height: '100%', overflowY: 'auto' }}>
            <Stack spacing={1}>
              <Box
                sx={{
                  width: '100%',
                  transition: 'all 400ms ease-in-out',
                }}
                pl={-1}
              >
                <NavigationButton
                  isCurrentPage={isPageOpen(SphereNavigationRoutes.Dashboard)}
                  isNavOpen={isNavOpen}
                  title={t('button-dashboard')}
                  onClick={() => navigateToRouteHandler(SphereNavigationRoutes.Dashboard)}
                  contextMenuOptions={[
                    {
                      title: t('button-open-in-new-tab'),
                      icon: <OpenInNewTabIcon color="action" />,
                      action: () =>
                        newTabClickHandler(
                          navigateSphereWithFilters(currentOrganizationId ?? '', true),
                        ),
                    },
                  ]}
                  icon={<DashboardIcon />}
                />
                <NavigationButton
                  isCurrentPage={isPageOpen(SphereNavigationRoutes.Statistics)}
                  isNavOpen={isNavOpen}
                  title={t('button-statistics')}
                  onClick={() => navigateToRouteHandler(SphereNavigationRoutes.Statistics)}
                  contextMenuOptions={[
                    {
                      title: t('button-open-in-new-tab'),
                      icon: <OpenInNewTabIcon color="action" />,
                      action: () =>
                        newTabClickHandler(
                          navigateSphereWithFilters(
                            `${currentOrganizationId}${SphereNavigationRoutes.Statistics}`,
                            true,
                          ),
                        ),
                    },
                  ]}
                  icon={<StatisticsIcon />}
                />
                <NavigationButton
                  isCurrentPage={isPageOpen(SphereNavigationRoutes.MissionHistory)}
                  isNavOpen={isNavOpen}
                  title={t('button-history')}
                  onClick={() => navigateToRouteHandler(SphereNavigationRoutes.MissionHistory)}
                  contextMenuOptions={[
                    {
                      title: t('button-open-in-new-tab'),
                      icon: <OpenInNewTabIcon color="action" />,
                      action: () =>
                        newTabClickHandler(
                          navigateSphereWithFilters(
                            `${currentOrganizationId}${SphereNavigationRoutes.MissionHistory}`,
                            true,
                          ),
                        ),
                    },
                  ]}
                  icon={<MissionHistoryIcon />}
                />
                <NavigationButton
                  isCurrentPage={isPageOpen(SphereNavigationRoutes.Users)}
                  isNavOpen={isNavOpen}
                  title={t('button-users')}
                  onClick={() => navigateToRouteHandler(SphereNavigationRoutes.Users)}
                  contextMenuOptions={[
                    {
                      title: t('button-open-in-new-tab'),
                      icon: <OpenInNewTabIcon color="action" />,
                      action: () =>
                        newTabClickHandler(
                          navigateSphereWithFilters(
                            `${currentOrganizationId}${SphereNavigationRoutes.Users}`,
                            true,
                          ),
                        ),
                    },
                  ]}
                  icon={<UsersIcon />}
                />

                {organizationData.current?.role !== OrganizationRole.LOCATION && (
                  <NavigationButton
                    isCurrentPage={isPageOpen(SphereNavigationRoutes.Organizations)}
                    isNavOpen={isNavOpen}
                    title={
                      organizationData.current?.role !== OrganizationRole.CUSTOMER
                        ? t('button-organizations')
                        : t('title-locations')
                    }
                    onClick={() => navigateToRouteHandler(SphereNavigationRoutes.Organizations)}
                    contextMenuOptions={[
                      {
                        title: t('button-open-in-new-tab'),
                        icon: <OpenInNewTabIcon color="action" />,
                        action: () =>
                          newTabClickHandler(
                            navigateSphereWithFilters(
                              `${currentOrganizationId}${SphereNavigationRoutes.Organizations}`,
                              true,
                            ),
                          ),
                      },
                    ]}
                    icon={<OrganizationsIcon />}
                  />
                )}
                <NavigationButton
                  isCurrentPage={isPageOpen(SphereNavigationRoutes.Settings)}
                  isNavOpen={isNavOpen}
                  title={t('button-settings')}
                  onClick={() => navigateToRouteHandler(SphereNavigationRoutes.Settings)}
                  contextMenuOptions={[
                    {
                      title: t('button-open-in-new-tab'),
                      icon: <OpenInNewTabIcon color="action" />,
                      action: () =>
                        newTabClickHandler(
                          navigateSphereWithFilters(
                            `${currentOrganizationId}${SphereNavigationRoutes.Settings}`,
                            true,
                          ),
                        ),
                    },
                  ]}
                  icon={<SettingsIcon />}
                />
                {organizationData.current?.role === OrganizationRole.MANUFACTURER && isAdmin && (
                  <NavigationButton
                    isCurrentPage={isPageOpen(SphereNavigationRoutes.Administration)}
                    isNavOpen={isNavOpen}
                    title={t('button-administration')}
                    onClick={() => navigateToRouteHandler(SphereNavigationRoutes.Administration)}
                    contextMenuOptions={[
                      {
                        title: t('button-open-in-new-tab'),
                        icon: <OpenInNewTabIcon color="action" />,
                        action: () =>
                          newTabClickHandler(
                            navigateSphereWithFilters(
                              `${currentOrganizationId}${SphereNavigationRoutes.Administration}`,
                              true,
                            ),
                          ),
                      },
                    ]}
                    icon={<AdministrationIcon />}
                  />
                )}
                {matchesLgDown &&
                  <React.Fragment>
                    <Divider sx={{ my: 1 }} />
                    <AdditionalButtons />
                  </React.Fragment>
                }
              </Box>
            </Stack>
          </Stack>
        </Stack>
      </Box>
    </React.Fragment>
  );
};

export default MainNavigationBar;
